import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Iframe from 'react-iframe';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ProgressMobileStepper from './ProgressMobileStepper';
import Lesson from './Lesson';
import { useMediaQuery, Snackbar } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import Paragraph from './Paragraph';
import CompletionPieChart from './CompletionPieChart';
import IframeComponent from './IframeComponent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

const { REACT_APP_ENDPOINT } = process.env;

const createSummaryLesson = (lessonDone, lessons, lessonSummary, isMobile) => ({
  title: 'Summary',
  content: (
    <>
      <Stack spacing={4} direction="row">
        <Stack spacing={2} sx={{ width: '100%' }}>
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CompletionPieChart title="Lesson Steps Completed" completed={lessonDone.filter(done => done).length} total={lessons.length} />
            </Box>
          )}
          <Typography variant="h6" sx={{ color: '#333333', fontWeight: 'bold', fontFamily: 'Roboto, sans-serif', fontSize: '16px' }}>Summary</Typography>
          <Paragraph>{lessonSummary}</Paragraph>
          <Typography variant="h6" sx={{ color: '#333333', fontWeight: 'bold', marginTop: 2, fontFamily: 'Roboto, sans-serif', fontSize: '16px' }}>Next Steps</Typography>
          <Paragraph>{`You've successfully completed `}<b>{lessonDone.filter(done => done).length}</b>{` out of `}<b>{lessons.length}</b>{` challenges in this lesson.`}</Paragraph>
          {lessonDone.filter(done => done).length < Math.ceil(lessons.length * 0.7) ? (
            <Paragraph>{`To finish this lesson, you need to complete at least `}<b>{Math.ceil(lessons.length * 0.8)}</b>{` steps. Please go back and solve `}<b>{Math.ceil(lessons.length * 0.8) - lessonDone.filter(done => done).length}</b>{` more steps to continue.`}</Paragraph>
          ) : (
            <Paragraph>{`You've completed enough steps to finish this lesson. Feel free to go back and revisit the commands to improve your score, or you can choose to finish the exercise now.`}</Paragraph>
          )}
        </Stack>
        {!isMobile && <CompletionPieChart title="Lesson Steps Completed" completed={lessonDone.filter(done => done).length} total={lessons.length} />}
      </Stack>
      {!isMobile && <Box sx={{ height: 100 }} />}
    </>
  ),
  validation: {}
});

const generateRandomSession = () => {
  return Math.random().toString().slice(2, 10);
};

const CourseComponent = ({ lessons, script, lessonName, lessonSummary }) => {
  const history = useHistory();
  const [lesson, setLesson] = useState(0);
  const [lessonDone, setLessonDone] = useState(Array(lessons.length + 1).fill(false)); // Adjusted for new lesson
  const [userUUID, setUserUUID] = useState(null);
  const [ws, setWs] = useState(null);
  const [lessonsWithSummary, setLessonsWithSummary] = useState([]);
  const [session, setSession] = useState(generateRandomSession());
  const reducedWidth = useMediaQuery('(max-width:1200px)');
  const isMobile = useMediaQuery('(max-width:600px)');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef(null);

  // Initialize lessonsWithSummary with the new "Finished" lesson
  useEffect(() => {
    const updatedLessons = [...lessons, createSummaryLesson(lessonDone, lessons, lessonSummary, isMobile)];
    setLessonsWithSummary(updatedLessons);
  }, [lessons, lessonDone, lessonSummary, isMobile]);

  useEffect(() => {
    if (isMobile) {
      setSnackbarOpen(true);
    }
  }, [isMobile]);

  const nextLesson = useCallback(() => {
    setLesson((prevLesson) => (prevLesson < lessonsWithSummary.length - 1 ? prevLesson + 1 : prevLesson));
  }, [lessonsWithSummary.length]);

  const prevLesson = useCallback(() => {
    setLesson((prevLesson) => (prevLesson > 0 ? prevLesson - 1 : prevLesson));
  }, []);

  const processConsole = useCallback((data) => {
    const { commandNeeded, arguments: args, type, matchInputHex, matchOutputHex, command, set_alias_name, 
      set_alias_value, remove_alias_name, set_function_name, remove_function_name, echo_value, echo_value_substring, type_value,
      set_variable_name, set_variable_value, remove_variable_name, export_variable, exit_builtin ,
      fg_job_number, bg_job_number, jobs_builtin, kill_builtin, help_value} = lessonsWithSummary[lesson].validation;
  
    if (data.step != lesson) {
      return;
    }

    switch (type) {
      case 'command_needed':
        if (data.data.length >= 3) {
          const [lastCommand] = data.data.slice(-3);
          let commandValid = false;
  
          if (commandNeeded && lastCommand.type === 'exec_command' && lastCommand.commandpath && lastCommand.commandpath === commandNeeded) {
            commandValid = true;
          }
  
          if (args && lastCommand.type === 'exec_command' && lastCommand.args && args.every(arg => lastCommand.args.includes(arg))) {
            commandValid = true;
          }

          if (set_alias_name && lastCommand.type === 'add_alias' && lastCommand.name === set_alias_name) {
            commandValid = true;
            if (set_alias_value) {
              if (lastCommand.value === set_alias_value){
                commandValid = true;
              } else {
                commandValid = false;
              }      
            }        
          }
  
          if (remove_alias_name && lastCommand.type === 'remove_alias' && lastCommand.name === remove_alias_name) {
            commandValid = true;      
          }
  
          if (set_function_name && lastCommand.type === 'add_function' && lastCommand.name === set_function_name) {
            commandValid = true;      
          }

          if (remove_function_name && lastCommand.type === 'remove_function' && lastCommand.name === remove_function_name) {
            commandValid = true;      
          }

          if (set_variable_name && lastCommand.type === 'add_variable' && lastCommand.name === set_variable_name) {
            commandValid = true;
            if (set_variable_value) {
              if (lastCommand.value === set_variable_value){
                commandValid = true;
              } else {
                commandValid = false;
              }      
            }        
          }

          if (remove_variable_name && lastCommand.type === 'remove_variable' && lastCommand.name === remove_variable_name) {
            commandValid = true;      
          }

          if (echo_value && lastCommand.type === 'echo_builtin' && lastCommand.value === echo_value) {
            commandValid = true;      
          }

          if (echo_value_substring && lastCommand.type === 'echo_builtin' && lastCommand.value && lastCommand.value.includes(echo_value_substring)) {
            commandValid = true;      
          }

          if (type_value && lastCommand.type === 'type_builtin' && lastCommand.value === type_value) {
            commandValid = true;      
          }

          if (help_value && lastCommand.type === 'help_builtin' && lastCommand.value === help_value) {
            commandValid = true;      
          }

          if (export_variable && lastCommand.type === 'export_builtin' && lastCommand.value === export_variable) {
            commandValid = true;      
          }

          if (bg_job_number && lastCommand.type === 'bg_builtin' && lastCommand.value === bg_job_number) {
            commandValid = true;      
          }

          if (fg_job_number && lastCommand.type === 'fg_builtin' && lastCommand.value === fg_job_number) {
            commandValid = true;      
          }

          if (kill_builtin && lastCommand.type === 'kill_builtin') {
            const killArgs = lastCommand.value.split(' ');
            if (!args || args.every(arg => killArgs.includes(arg))) {
              commandValid = true;
            }
          }

          if (exit_builtin && lastCommand.type === 'exit_builtin') {
            commandValid = true;      
          }

          if (jobs_builtin && lastCommand.type === 'jobs_builtin') {
            commandValid = true;      
          }

          if (commandValid) {
            setLessonDone((prevLessonDone) => {
              const newLessonDone = [...prevLessonDone];
              newLessonDone[lesson] = true;
              return newLessonDone;
            });
          }
        }
        break;
      case 'input_output_matches':
          if (data.data.length >= 3) {
            const [lastCommand] = data.data.slice(-3);
            const [lastOutput] = data.data.slice(-2);
            const [lastInput] = data.data.slice(-1);
            let matchValid = false;
    
            if (lastOutput && lastInput) {
              const parseHex = (hexString) => (typeof hexString === 'string' ? hexString.match(/.{1,2}/g) : []) || [];
              const parsedLastInput = parseHex(lastInput.Data);
              const parsedLastOutput = parseHex(lastOutput.Data);
    
              if (matchInputHex && matchInputHex.every(hex => hex.length > 2 ? lastInput.Data.includes(hex) : parsedLastInput.includes(hex))) {
                matchValid = true;
              }

              if (matchOutputHex){
                matchValid = false;
                if (matchOutputHex.every(hex => hex.length > 2 ? lastOutput.Data.includes(hex) : parsedLastOutput.includes(hex))) {
                  matchValid = true;
                }
              }
            }

            if(matchValid && command) {
              if(command.every(arg => lastCommand.args.includes(arg)))
                matchValid = true; 
              else
                matchValid = false;
            }

            if (matchValid) {
              setLessonDone((prevLessonDone) => {
                const newLessonDone = [...prevLessonDone];
                newLessonDone[lesson] = true;
                return newLessonDone;
              });
            }
          }
          break;
      case 'textfiled_matches_exec':
        if (data.data.stdout) {
          const textFieldShouldMatch = data.data.stdout.trim();
          const textFiledText = document.getElementById(lessonsWithSummary[lesson].validation.textFiledId).value;
          if (textFieldShouldMatch === textFiledText) {
            setLessonDone((prevLessonDone) => {
              const newLessonDone = [...prevLessonDone];
              newLessonDone[lesson] = true;
              return newLessonDone;
            });
          }
        }
        break;
      case 'radiobutton_matches_exec':
        if (data.data.stdout) {
          const radioButtonShouldMatch = data.data.stdout.trim();
          const radioElement = document.querySelector(`[name="${lessonsWithSummary[lesson].validation.formName}"]:checked`);
          if (radioElement && radioButtonShouldMatch === radioElement.value) {
            setLessonDone((prevLessonDone) => {
              const newLessonDone = [...prevLessonDone];
              newLessonDone[lesson] = true;
              return newLessonDone;
            });
          }
        }
        break;
      case 'exec_command':
        if (lessonsWithSummary[lesson].validation.exitCode === data.data.exitcode) {
          setLessonDone((prevLessonDone) => {
            const newLessonDone = [...prevLessonDone];
            newLessonDone[lesson] = true;
            return newLessonDone;
          });
        }
        break;
      default:
        break;
    }
  }, [lesson, lessonsWithSummary]);

  const fetchData = useCallback(async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return response.json();
  }, []);

  const loadData = useCallback(async () => {
    if (!userUUID) return;

    const { validation } = lessonsWithSummary[lesson];
    const url = `${REACT_APP_ENDPOINT}/api/environment/term/${userUUID}/exec`;
    const body = { command: validation.execCommand };

    switch (validation.type) {
      case 'radiobutton_matches_exec':
        if (ws) {
          ws.send(JSON.stringify({ type: 'exec_command', command: validation.execCommand, step: lesson }));
        }
        break;
      case 'textfiled_matches_exec':
        if (ws) {
          ws.send(JSON.stringify({ type: 'exec_command', command: validation.execCommand, step: lesson }));
        }
        break;
      case 'exec_command':
        if (ws) {
          ws.send(JSON.stringify({ type: 'exec_command', command: validation.execCommand, step: lesson }));
        }
        break;
      case 'command_needed':
        if (ws) {
          ws.send(JSON.stringify({ type: 'fetch_console_data', step: lesson }));
        }
        break;
      case 'input_output_matches':
        if (ws) {
          ws.send(JSON.stringify({ type: 'fetch_console_data', step: lesson }));
        }
        break;
      default:
        break;
    }
  }, [lesson, fetchData, userUUID, lessonsWithSummary, ws]);

  useEffect(() => {
    if (ws) {
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        processConsole(data);
      };
    }
  
    return () => {
      if (ws) {
        ws.onmessage = null;
      }
    };
  }, [ws, processConsole]);

  useEffect(() => {
    const interval = setInterval(loadData, 1000);
    return () => clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    const installEnvironment = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userUUID = userData.uuid;
          setUserUUID(userUUID);
          await fetch(`https://${REACT_APP_ENDPOINT}/api/environment/install`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uuid: userUUID, command: script, session: session }),
          });

          const establishWebSocket = () => {
            const websocket = new WebSocket(`wss://${REACT_APP_ENDPOINT}/api/terminal/${userUUID}-${session}/data`);
            websocket.onopen = () => {
              setWs(websocket);
            };
            websocket.onerror = () => {
              setTimeout(establishWebSocket, 1000); // Retry after 1 second
            };
          };
  
          establishWebSocket();
        } else {
          console.error("User document does not exist");
        }
      }
    };

    installEnvironment();

    return async () => {
      if (userUUID) {
        await fetch(`https://${REACT_APP_ENDPOINT}/api/environment/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uuid: userUUID }),
        });
      }
      if (ws) {
        ws.close();
      }
    };
  }, [script]);

  const handleBackClick = () => {
    const currentPath = history.location.pathname;
    const pathParts = currentPath.split('/');
    
    if (pathParts[1] === 'module' && pathParts[3] === 'lesson') {
      const newPath = pathParts.slice(0, -2).join('/');
      history.push(newPath);
    } else {
      const newPath = pathParts.slice(0, -1).join('/');
      history.push(newPath);
    }
  };

  useEffect(() => {
    const handleClick = (event) => {
      if (event.target.tagName === 'CODE') {
        navigator.clipboard.writeText(event.target.innerText);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      setHasOverflow(scrollHeight > clientHeight);
    }
  }, [lessonsWithSummary, lesson]);

  const renderHeader = () => (
      <Box sx={{ display: 'flex', alignItems: 'center', paddingBottom: 3 }}>
        <Box onClick={handleBackClick} sx={{ cursor: 'pointer' }}>
          <PlayCircleOutlineIcon 
            sx={{ 
              marginRight: 2, 
              color: '#006666', 
              transform: 'rotate(180deg)', 
              fontSize: '3rem',
              '&:hover': {
                color: '#02a8a8'
              }
            }} 
          />
        </Box>
        <Typography variant="h1" sx={{ fontSize: 28, fontWeight: 'bold', color: '#333333'  }}>
          {lessonsWithSummary[lesson] ? `${lessonName} - ${lessonsWithSummary[lesson].title}${lesson < lessons.length ? ` - (${lesson + 1}/${lessons.length})` : ''}` : 'Loading...'}
        </Typography>
      </Box>
  );

  const renderLessonContent = (mobile) => (
    <>
      <Box 
        ref={contentRef}
        sx={{ 
          flexGrow: 1,
          ...(!mobile && {
            height: '526px',
          })
        }}>
        {mobile && <Box sx={{ height: 12 }} />}
        {lessonsWithSummary[lesson] && (
          <Lesson 
            done={lessonDone[lesson]} 
            hint={lessonsWithSummary[lesson].validation.hint} 
            showAlert={Object.keys(lessonsWithSummary[lesson].validation).length > 0}
          >
            {lessonsWithSummary[lesson].content}
            {mobile && <Box sx={{ height: 24 }} />}
          </Lesson>
        )}
      </Box>
      <Box sx={{ height: 20 }} />
      <ProgressMobileStepper 
        steps={lessonsWithSummary.length} 
        onNext={nextLesson} 
        onPrev={prevLesson} 
        disableFinish={lessonDone.filter(done => done).length < Math.ceil(lessons.length * 0.7)}
        sx={{ paddingTop: 8, flexGrow: 0 }}
      />
      {mobile && <Box sx={{ height: 24 }} />}
    </>
  );

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        {renderHeader()}
      </Grid>
        <>
          {!isMobile && 
            <Grid item xs={lesson === lessonsWithSummary.length - 1 ? 12 : 4} sx={{ paddingRight: reducedWidth ? 2 : 6, display: 'flex', flexDirection: 'column' }}>
              {renderLessonContent(false)}
            </Grid>
          }
          <Grid item xs={ isMobile ? 12 : 8 } sx={{ display: lesson === lessonsWithSummary.length - 1 ? 'none' : 'block' }}>
            <IframeComponent userUUID={userUUID} session={session} />
          </Grid>
          {isMobile &&
            <Grid item xs={12}>
              {renderLessonContent(true)}
            </Grid>
          }
        </>
        <Snackbar
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          message={
            <span style={{ color: 'white', fontFamily: 'Roboto', display: 'flex', alignItems: 'center' }}>
              For the best experience, we advise using the application on a desktop computer.
              <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbarOpen(false)} sx={{ marginLeft: 1 }}>
                <CloseIcon fontSize="medium" sx={{ color: 'white' }} />
              </IconButton>
            </span>
          }
          autoHideDuration={6000}
          ContentProps={{
            sx: {
              boxShadow: 'none',
              borderRadius: '8px',
              backgroundColor: '#006666',
            },
          }}
        />

    </Grid>
  );
};

export default CourseComponent;